/**
 * Настроить кадр — visual crop editor for editorial media.
 *
 * The caller supplies the actual frame aspect ratio for the current MASTER,
 * composition variant and device mode. The crop editor does not own layout
 * geometry and therefore cannot silently impose one universal crop.
 */
import { useEffect, useRef, useState } from "react";
import { resolveMediaUrl } from "@/lib/editorialMedia.mjs";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export default function CropModal({
  open,
  mode,
  url,
  aspectRatio = 4 / 5,
  frameLabel,
  focalX = 0.5,
  focalY = 0.5,
  zoom = 1,
  onSave,
  onClose,
}) {
  const [fx, setFx] = useState(focalX);
  const [fy, setFy] = useState(focalY);
  const [z, setZ] = useState(zoom || 1);
  const frameRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (open) { setFx(focalX ?? 0.5); setFy(focalY ?? 0.5); setZ(zoom || 1); }
  }, [open, focalX, focalY, zoom]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const aspect = Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 4 / 5;
  const src = resolveMediaUrl(url);

  const beginDrag = (clientX, clientY) => {
    const frame = frameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    dragRef.current = { startX: clientX, startY: clientY, startFx: fx, startFy: fy, w: rect.width, h: rect.height };
  };
  const moveDrag = (clientX, clientY) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (clientX - d.startX) / d.w;
    const dy = (clientY - d.startY) / d.h;
    setFx(clamp(d.startFx - dx / z, 0, 1));
    setFy(clamp(d.startFy - dy / z, 0, 1));
  };
  const endDrag = () => { dragRef.current = null; };

  const onMouseDown = (e) => { e.preventDefault(); beginDrag(e.clientX, e.clientY); };
  const onMouseMove = (e) => { if (dragRef.current) moveDrag(e.clientX, e.clientY); };
  const onMouseUp = () => endDrag();
  const onTouchStart = (e) => { const t = e.touches[0]; beginDrag(t.clientX, t.clientY); };
  const onTouchMove = (e) => { e.preventDefault(); const t = e.touches[0]; moveDrag(t.clientX, t.clientY); };
  const onTouchEnd = () => endDrag();

  const reset = () => { setFx(0.5); setFy(0.5); setZ(1); };
  const done = () => { onSave({ focal_x: fx, focal_y: fy, zoom: z }); };

  const imageStyle = {
    width: "100%", height: "100%",
    objectFit: "cover",
    objectPosition: `${fx * 100}% ${fy * 100}%`,
    transform: `scale(${z})`,
    transformOrigin: `${fx * 100}% ${fy * 100}%`,
    transition: dragRef.current ? "none" : "transform .12s ease-out",
    userSelect: "none",
    pointerEvents: "none",
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-3 py-6"
      onMouseUp={onMouseUp}
      onMouseMove={onMouseMove}
      data-testid="hero-crop-modal">
      <div className="w-full max-w-[520px] bg-cream rounded-2xl overflow-hidden shadow-xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-espresso/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">Настроить кадр</div>
            <div className="font-serif text-lg text-espresso">
              {frameLabel || (mode === "mobile" ? "Мобильная композиция" : "Настольная композиция")}
            </div>
          </div>
          <button onClick={onClose}
            className="text-sm opacity-70 hover:opacity-100"
            data-testid="hero-crop-cancel">Отмена</button>
        </div>

        <div className="p-4">
          <p className="mb-3 text-xs leading-relaxed text-espresso/70" data-testid="hero-crop-help">
            Рамка соответствует выбранной композиции. Перемещайте фотографию и меняйте приближение — исходные пропорции сохраняются, растягивания не будет.
          </p>
          <div
            ref={frameRef}
            className="relative mx-auto overflow-hidden rounded-lg bg-neutral-100 touch-none select-none"
            style={{ width: "100%", aspectRatio: aspect }}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            data-testid="hero-crop-frame">
            {src && <img src={src} alt="" draggable={false} style={imageStyle} />}
            <div className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="border border-white/25" />
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-widest opacity-70">Приближение</span>
            <input type="range" min="1" max="3" step="0.05" value={z}
              onChange={(e) => setZ(parseFloat(e.target.value))}
              className="flex-1 accent-[#3B241A]"
              data-testid="hero-crop-zoom" />
            <span className="w-10 text-right text-[12px] opacity-70">{z.toFixed(1)}x</span>
          </div>

          <div className="mt-4 flex items-center justify-between gap-2">
            <button
              onClick={reset}
              className="px-4 py-2 rounded-xl border border-espresso/25 text-sm"
              data-testid="hero-crop-reset">Сбросить</button>
            <button
              onClick={done}
              className="px-6 py-2 rounded-xl bg-espresso text-cream text-sm tracking-wide"
              data-testid="hero-crop-done">Готово</button>
          </div>

          <div className="mt-3 text-[11px] opacity-60 text-center">
            Двигайте фото пальцем или мышкой, чтобы разместить главный объект в кадре.
          </div>
        </div>
      </div>
    </div>
  );
}
